import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './AdminDashboard.css';

const AGE_LABELS = {
  under_18: 'Under 18', '18_24': '18–24', '25_34': '25–34',
  '35_44': '35–44', '45_54': '45–54', '55_64': '55–64', '65_plus': '65+'
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('registrations'); // 'registrations' | 'stats' | 'users'
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState({ username: '', password: '' });
  const [userMsg, setUserMsg] = useState({ text: '', type: '' });
  const [changePwId, setChangePwId] = useState(null);
  const [newPw, setNewPw] = useState('');
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const LIMIT = 25;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/registrations', {
        params: { page, limit: LIMIT, search, country: filterCountry, user_role: filterRole }
      });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, filterCountry, filterRole]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (tab === 'stats') fetchStats(); }, [tab, fetchStats]);
  useEffect(() => { if (tab === 'users') fetchUsers(); }, [tab, fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserMsg({ text: '', type: '' });
    try {
      await api.post('/admin/users', userForm);
      setUserMsg({ text: `User "${userForm.username}" created successfully!`, type: 'success' });
      setUserForm({ username: '', password: '' });
      fetchUsers();
    } catch (err) {
      setUserMsg({ text: err.response?.data?.error || 'Failed to create user', type: 'error' });
    }
  };

  const handleDeleteUser = async (id, username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers();
    } catch (err) { alert('Failed to delete user'); }
  };

  const handleChangePassword = async (id) => {
    if (!newPw || newPw.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    try {
      await api.put(`/admin/users/${id}/password`, { password: newPw });
      setChangePwId(null);
      setNewPw('');
      alert('Password updated!');
    } catch (err) { alert('Failed to update password'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('cev_admin_token');
    navigate('/admin/login');
  };

  const handleExport = (format) => {
    const token = localStorage.getItem('cev_admin_token');
    const params = new URLSearchParams({ format, country: filterCountry, user_role: filterRole });
    const url = `${process.env.REACT_APP_API_URL || '/api'}/admin/export?${params}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `cev_registrations.${format}`);
    // Need auth header — use fetch
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const burl = URL.createObjectURL(blob);
        a.href = burl;
        a.click();
        URL.revokeObjectURL(burl);
      });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/registrations/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (e) { alert('Delete failed'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-logo">
          <span className="sidebar-logo-text">CEV</span>
          <span className="sidebar-logo-sub">Admin</span>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${tab === 'registrations' ? 'active' : ''}`}
            onClick={() => setTab('registrations')}
          >
            <span>📋</span> Registrations
          </button>
          <button
            className={`nav-item ${tab === 'stats' ? 'active' : ''}`}
            onClick={() => setTab('stats')}
          >
            <span>📊</span> Statistics
          </button>
          <button
            className={`nav-item ${tab === 'users' ? 'active' : ''}`}
            onClick={() => setTab('users')}
          >
            <span>👥</span> Users
          </button>
        </nav>
        <div className="sidebar-footer">
          <a href="/" className="nav-item">
            <span>🌐</span> View Portal
          </a>
          <button className="nav-item nav-logout" onClick={handleLogout}>
            <span>🚪</span> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {tab === 'registrations' && (
          <>
            <div className="admin-topbar">
              <div>
                <h2>Registrations</h2>
                <p>{total} total records</p>
              </div>
              <div className="export-btns">
                <button className="btn-export" onClick={() => handleExport('xlsx')}>⬇ Export Excel</button>
                <button className="btn-export btn-export-csv" onClick={() => handleExport('csv')}>⬇ Export CSV</button>
              </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
              <input
                type="search"
                placeholder="Search name or email…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="filter-input filter-search"
              />
              <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setPage(1); }} className="filter-input">
                <option value="">All Countries</option>
                {/* Dynamically populated from data would be better, but static list works */}
                {[...new Set(data.map(d => d.country))].sort().map(c =>
                  <option key={c} value={c}>{c}</option>
                )}
              </select>
              <select value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }} className="filter-input">
                <option value="">All Roles</option>
                {['coach','teacher','referee','volunteer','parent'].map(r =>
                  <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                )}
              </select>
            </div>

            {/* Table */}
            <div className="table-wrap">
              <table className="reg-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Country</th>
                    <th>Age</th>
                    <th>Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="table-loading">Loading…</td></tr>
                  ) : data.length === 0 ? (
                    <tr><td colSpan={8} className="table-loading">No registrations found</td></tr>
                  ) : data.map(row => (
                    <tr key={row.id}>
                      <td className="td-id">#{row.id}</td>
                      <td className="td-name">{row.first_name} {row.last_name}</td>
                      <td className="td-email">{row.email}</td>
                      <td><span className={`role-badge role-${row.user_role}`}>{row.user_role}</span></td>
                      <td>{row.country}</td>
                      <td>{AGE_LABELS[row.age_bracket] || row.age_bracket}</td>
                      <td>{new Date(row.created_at).toLocaleDateString()}</td>
                      <td className="td-actions">
                        <button className="btn-view" onClick={() => setSelected(row)}>View</button>
                        <button className="btn-delete" onClick={() => setDeleteId(row.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                <span>Page {page} of {totalPages}</span>
                <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            )}
          </>
        )}

        {tab === 'stats' && stats && (
          <div className="stats-grid">
            <div className="stat-card stat-big">
              <div className="stat-num">{stats.total}</div>
              <div className="stat-label">Total Registrations</div>
            </div>
            <div className="stat-card stat-big">
              <div className="stat-num">{stats.today}</div>
              <div className="stat-label">Today</div>
            </div>

            <div className="stat-card">
              <h3>By Role</h3>
              {stats.byRole.map(r => (
                <div key={r.user_role} className="stat-bar-row">
                  <span>{r.user_role}</span>
                  <div className="stat-bar-bg">
                    <div className="stat-bar-fill" style={{ width: `${Math.round(r.count / stats.total * 100)}%` }} />
                  </div>
                  <span className="stat-count">{r.count}</span>
                </div>
              ))}
            </div>

            <div className="stat-card">
              <h3>Top 10 Countries</h3>
              {stats.byCountry.map(c => (
                <div key={c.country} className="stat-bar-row">
                  <span>{c.country}</span>
                  <div className="stat-bar-bg">
                    <div className="stat-bar-fill" style={{ width: `${Math.round(c.count / stats.total * 100)}%` }} />
                  </div>
                  <span className="stat-count">{c.count}</span>
                </div>
              ))}
            </div>

            <div className="stat-card">
              <h3>By Age Bracket</h3>
              {stats.byAge.map(a => (
                <div key={a.age_bracket} className="stat-bar-row">
                  <span>{AGE_LABELS[a.age_bracket] || a.age_bracket}</span>
                  <div className="stat-bar-bg">
                    <div className="stat-bar-fill stat-bar-gold" style={{ width: `${Math.round(a.count / stats.total * 100)}%` }} />
                  </div>
                  <span className="stat-count">{a.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === 'stats' && !stats && <p style={{ padding: 24 }}>Loading stats…</p>}

        {tab === 'users' && (
          <div className="users-panel">
            {/* Create new user */}
            <div className="users-create-card">
              <h3>Create New Admin User</h3>
              <form onSubmit={handleCreateUser} className="users-form">
                <div className="form-field">
                  <label>Username</label>
                  <input
                    type="text" placeholder="username"
                    value={userForm.username}
                    onChange={e => setUserForm(f => ({ ...f, username: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Password <span style={{color:'var(--gray-500)', fontWeight:400, fontSize:'12px'}}>(min. 8 characters)</span></label>
                  <input
                    type="password" placeholder="••••••••"
                    value={userForm.password}
                    onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))}
                    required minLength={8}
                  />
                </div>
                <button type="submit" className="btn-export">+ Create User</button>
              </form>
              {userMsg.text && (
                <p className={`user-msg user-msg-${userMsg.type}`}>{userMsg.text}</p>
              )}
            </div>

            {/* Existing users list */}
            <div className="users-list-card">
              <h3>Existing Admin Users</h3>
              {users.length === 0 ? (
                <p className="users-empty">No database users yet. The master admin is set via the .env file on the server.</p>
              ) : (
                <table className="reg-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td className="td-id">#{u.id}</td>
                        <td className="td-name">{u.username}</td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        <td className="td-actions">
                          <button className="btn-view" onClick={() => { setChangePwId(u.id); setNewPw(''); }}>
                            Change Password
                          </button>
                          <button className="btn-delete" onClick={() => handleDeleteUser(u.id, u.username)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selected.first_name} {selected.last_name}</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              {[
                ['ID', `#${selected.id}`],
                ['Email', selected.email],
                ['Role', selected.user_role],
                ['Country', selected.country],
                ['Age Bracket', AGE_LABELS[selected.age_bracket]],
                ['Phone', selected.phone || '—'],
                ['Areas of Interest', Array.isArray(selected.areas_of_interest)
                  ? selected.areas_of_interest.join(', ')
                  : (selected.areas_of_interest ? JSON.parse(selected.areas_of_interest).join(', ') : '—')],
                ['Playing Format', selected.playing_format || '—'],
                ['Competition Level', selected.competition_level || '—'],
                ['Match Date', selected.match_date || '—'],
                ['Match Time', selected.match_time || '—'],
                ['Internal Match #', selected.internal_match_number || '—'],
                ['Home Team', selected.home_team_name || '—'],
                ['Away Team', selected.away_team_name || '—'],
                ['Age Group', selected.age_group || '—'],
                ['Gender', selected.gender || '—'],
                ['Registered', new Date(selected.created_at).toLocaleString()],
                ['VolleyStation Token', selected.public_token || '—'],
              ].map(([k, v]) => (
                <div key={k} className="modal-row">
                  <span className="modal-key">{k}</span>
                  <span className="modal-val">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changePwId && (
        <div className="modal-overlay" onClick={() => setChangePwId(null)}>
          <div className="modal-box modal-confirm" onClick={e => e.stopPropagation()}>
            <h3>Change Password</h3>
            <p>Enter a new password for this user (min. 8 characters).</p>
            <input
              type="password"
              placeholder="New password"
              value={newPw}
              onChange={e => setNewPw(e.target.value)}
              style={{ width:'100%', padding:'10px 14px', border:'1.5px solid var(--gray-200)', borderRadius:'8px', fontSize:'15px', marginBottom:'16px' }}
              autoFocus
            />
            <div className="confirm-btns">
              <button className="btn-cancel" onClick={() => setChangePwId(null)}>Cancel</button>
              <button className="btn-export" onClick={() => handleChangePassword(changePwId)}>Update Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal-box modal-confirm" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete registration #{deleteId}? This cannot be undone.</p>
            <div className="confirm-btns">
              <button className="btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn-delete-confirm" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
